import type { PlanType, SubscriptionInfo, UserSubscriptionStatus } from '@eci-saas/shared'
import { useSupabaseAuth, useSupabaseDatabase } from '~/composables/utils/useSupabase'

/**
 * Pure data fetching functions for authentication
 * These functions don't use i18n and can be called from async contexts
 */
export function useAuthData() {
  const supabaseAuth = useSupabaseAuth()
  const supabaseDb = useSupabaseDatabase()

  // 获取用户订阅信息
  const fetchUserSubscription = async (userId: string): Promise<{
    subscription: SubscriptionInfo | null
    status: UserSubscriptionStatus
  }> => {
    try {
      const { data, error } = await supabaseDb
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('start_date', { ascending: false })
        .limit(1)
        .single()

      if (error) {
        console.error('Error fetching subscription:', error)
        // 如果没有找到订阅记录，使用默认的免费套餐
        return {
          subscription: null,
          status: {
            isActive: true,
            currentPlan: 'free',
            expiresAt: null,
            canUpgrade: true,
          },
        }
      }

      const subscription: SubscriptionInfo = {
        id: data.id,
        userId: data.user_id,
        plan: data.plan,
        startDate: data.start_date,
        endDate: data.end_date,
        organizationId: data.organization_id,
      }

      // 计算订阅状态
      const now = new Date()
      const endDate = data.end_date ? new Date(data.end_date) : null
      const isActive = !endDate || endDate > now

      const status: UserSubscriptionStatus = {
        isActive,
        currentPlan: data.plan as PlanType,
        expiresAt: data.end_date,
        canUpgrade: data.plan === 'free' || (data.plan !== 'enterprise'),
      }

      return { subscription, status }
    }
    catch (error) {
      console.error('Failed to fetch subscription:', error)
      // 出错时使用默认设置
      return {
        subscription: null,
        status: {
          isActive: true,
          currentPlan: 'free',
          expiresAt: null,
          canUpgrade: true,
        },
      }
    }
  }

  // 获取当前用户会话
  const getCurrentSession = async () => {
    try {
      const { data: { session }, error } = await supabaseAuth.getSession()

      if (error) {
        console.error('Error getting session:', error)
        return null
      }

      return session
    }
    catch (error) {
      console.error('Failed to get current session:', error)
      return null
    }
  }

  // 刷新用户信息
  const refreshUserInfo = async () => {
    try {
      const { data, error } = await supabaseAuth.getUser()
      if (error) {
        console.error('Error refreshing user:', error)
        return null
      }
      return data.user
    }
    catch (error) {
      console.error('Failed to refresh user:', error)
      return null
    }
  }

  return {
    fetchUserSubscription,
    getCurrentSession,
    refreshUserInfo,
  }
}
